class PlpEmpty extends HTMLElement {
  connectedCallback() {
    this.swiperEl = this.querySelector('.plp-empty__swiper');
    if (this.swiperEl) this.initSwiper();
    this.initTouchHover();
  }

  initTouchHover() {
    // No (hover/pointer) media-query gate: some Android phones report
    // `hover: hover`, and touch events only fire on touch input anyway.
    const slides = this.querySelectorAll('.plp-empty__slide.slide-has-hover-img');
    // Set on touchstart, read on the click that follows the same tap.
    let revealTap = false;

    const activate = (slide) => {
      slides.forEach(s => s.classList.toggle('touch-active', s === slide));
    };

    // First touch on the image only reveals the second image; the link opens
    // on the next tap. The CTA pill always navigates straight away.
    const reveal = (slide, e) => {
      if (slide.classList.contains('touch-active')) return;
      revealTap = !e.target.closest('.plp-empty__cta-label');
      activate(slide);
    };

    // pointerdown and touchstart both fire for one tap; treat them as one press.
    const PRESS_DEDUPE_MS = 300;
    let lastPressAt = -Infinity;

    slides.forEach(slide => {
      // Listen on the whole slide so the CTA pill over the image counts too.
      const onPress = (e) => {
        if (e.type === 'pointerdown' && e.pointerType === 'mouse') return;
        const now = performance.now();
        if (now - lastPressAt < PRESS_DEDUPE_MS) return;
        lastPressAt = now;
        if (slide.classList.contains('touch-active')) {
          revealTap = false;
          return;
        }
        reveal(slide, e);
      };
      slide.addEventListener('touchstart', onPress, { passive: true });
      slide.addEventListener('pointerdown', onPress, { passive: true });

      // iOS Safari swallows touchstart for the touch that halts a momentum
      // scroll (fast fling), so catch the same gesture from its later events.
      const onLateTouch = (e) => reveal(slide, e);
      slide.addEventListener('touchmove', onLateTouch, { passive: true });
      slide.addEventListener('touchend', onLateTouch, { passive: true });

      slide.addEventListener('click', (e) => {
        if (!revealTap) return;
        revealTap = false;
        e.preventDefault();
      });
    });

    // Touching anywhere outside the active card returns it to the first image.
    // pointerdown backs up touchstart for the momentum-halting touch on iOS.
    const clearOutside = (e) => {
      if (e.type === 'pointerdown' && e.pointerType === 'mouse') return;
      slides.forEach(s => {
        if (!s.contains(e.target)) s.classList.remove('touch-active');
      });
    };
    document.addEventListener('touchstart', clearOutside, { passive: true });
    document.addEventListener('pointerdown', clearOutside, { passive: true });
  }

  // iOS only, on the native overflow rail. While Safari runs its own momentum
  // scroll it withholds every touch event until a long press, so touching a
  // card mid-glide couldn't swap to its second image. Here Safari only pans
  // vertically (touch-action: pan-y); the horizontal drag and glide are
  // applied to rail.scrollLeft from JS, so touches always reach the page.
  initIOSRailDrag(rail) {
    rail.classList.add('plp-empty__swiper--ios-drag');

    const AXIS_LOCK_PX = 6;
    // A swipe counts as horizontal unless clearly vertical, so a slightly
    // diagonal swipe no longer locks the rail (read as "stuck").
    const HORIZONTAL_BIAS = 0.7;
    // iOS native "normal" scroll deceleration: velocity kept per ms. Gives the
    // same long free glide as Safari's own momentum.
    const DECELERATION = 0.998;
    const MIN_VELOCITY = 0.01; // px/ms
    const VELOCITY_WINDOW_MS = 100; // release speed = finger speed over this window

    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let axis = null;
    let dragged = false;
    let rafId = null;
    let samples = [];
    // Float position: iOS rounds scrollLeft, so reading it back each frame
    // stalled slow glides (the value looked unchanged → glide stopped).
    let pos = 0;

    const maxLeft = () => Math.max(0, rail.scrollWidth - rail.clientWidth);
    const setPos = (value) => {
      pos = Math.min(Math.max(value, 0), maxLeft());
      rail.scrollLeft = pos;
    };

    const stopGlide = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
    };

    const glide = (velocity) => {
      let prev = performance.now();
      const step = (now) => {
        const dt = Math.min(now - prev, 32);
        prev = now;
        setPos(pos + velocity * dt);
        velocity *= Math.pow(DECELERATION, dt);
        // Stop when slow enough or pinned against either edge.
        const atEdge = pos <= 0 || pos >= maxLeft();
        if (Math.abs(velocity) < MIN_VELOCITY || atEdge) {
          rafId = null;
          return;
        }
        rafId = requestAnimationFrame(step);
      };
      rafId = requestAnimationFrame(step);
    };

    rail.addEventListener('touchstart', (e) => {
      stopGlide();
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      startLeft = pos = rail.scrollLeft;
      samples = [{ x: t.clientX, t: e.timeStamp }];
      axis = null;
      dragged = false;
    }, { passive: true });

    // Not passive: once a swipe is horizontal, stop Safari from also panning
    // the page vertically underneath it.
    rail.addEventListener('touchmove', (e) => {
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (!axis) {
        if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
        axis = Math.abs(dx) >= Math.abs(dy) * HORIZONTAL_BIAS ? 'x' : 'y';
      }
      if (axis !== 'x') return;
      if (e.cancelable) e.preventDefault();
      dragged = true;
      setPos(startLeft - dx);
      samples.push({ x: t.clientX, t: e.timeStamp });
      while (samples.length > 2 && e.timeStamp - samples[0].t > VELOCITY_WINDOW_MS) samples.shift();
    }, { passive: false });

    rail.addEventListener('touchend', (e) => {
      if (axis !== 'x') return;
      const first = samples[0];
      const last = samples[samples.length - 1];
      const dt = last.t - first.t;
      // Finger held still before lifting → no glide.
      if (dt <= 0 || e.timeStamp - last.t > VELOCITY_WINDOW_MS) return;
      const velocity = (first.x - last.x) / dt;
      if (Math.abs(velocity) >= MIN_VELOCITY) glide(velocity);
    }, { passive: true });

    // A horizontal drag must not open the card link it ended on.
    rail.addEventListener('click', (e) => {
      if (!dragged) return;
      dragged = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);
  }

  initSwiper() {
    const isIOS = /iP(hone|od|ad)/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (window.matchMedia('(max-width: 767px)').matches) {
      // Mobile: no Swiper; the browser scrolls the rail natively (overflow),
      // same as the other carousels. Swiper's JS touch handling fights the
      // page's native scroll on Android.
      this.swiperEl.classList.add('plp-empty__swiper--native-scroll');
      if (isIOS) this.initIOSRailDrag(this.swiperEl);
      return;
    }

    // This rail sits below the empty-state copy, so Swiper is fetched on
    // demand rather than parsed on page load.
    if (typeof Swiper === 'undefined') {
      window.loadSwiper().then(() => this.initSwiper());
      return;
    }

    this.swiper = new Swiper(this.swiperEl, {
      loop: false,
      slidesPerView: 'auto',
      spaceBetween: 1.93,
      simulateTouch: true,
      // threshold 0 reads any 1px jitter during a tap as a drag, so
      // Swiper's preventClicks swallows the resulting click and the
      // product link needs a second, motion-free click to register.
      threshold: 10,
      touchAngle: 70,
      freeMode: {
        enabled: true,
        sticky: false,
      },
      // Safari freezes page scroll with forceToAxis mousewheel; iOS has no
      // wheel anyway.
      mousewheel: isIOS ? false : {
        forceToAxis: true,
        sensitivity: 1,
        releaseOnEdges: true,
      },
      breakpoints: {
        768: {
          spaceBetween: 2,
        },
      },
    });
  }

  disconnectedCallback() {
    if (this.swiper) this.swiper.destroy(true, true);
  }
}

customElements.define('plp-empty', PlpEmpty);
